import { FC, useState, useRef, useEffect } from "react"
import { Contact, MessageType } from "./utils"
import { Icon, IconColor } from "@/components/shared/Icon"
import { capitalize } from "@/utils/lodash"

interface ContactAutocompleteProps {
   contacts: Contact[]
   messageType: MessageType
   selectedRecipients: string[]
   onSelectionChange: (recipients: string[]) => void
}

const ContactAutocomplete: FC<ContactAutocompleteProps> = ({
   contacts,
   messageType,
   selectedRecipients,
   onSelectionChange,
}) => {
   const [inputValue, setInputValue] = useState("")
   const [showDropdown, setShowDropdown] = useState(false)
   const wrapperRef = useRef<HTMLDivElement>(null)
   const inputRef = useRef<HTMLInputElement>(null)

   const selectedContacts = contacts.filter((contact) =>
      selectedRecipients.includes(contact.id)
   )

   const filteredContacts = contacts.filter((contact) => {
      if (!inputValue) return true

      const searchLower = inputValue.toLowerCase()
      return (
         contact.name.toLowerCase().includes(searchLower) ||
         contact.email.toLowerCase().includes(searchLower) ||
         contact.role.toLowerCase().includes(searchLower) ||
         contact.phone.includes(searchLower)
      )
   })

   const handleRemoveContact = (idToRemove: string) => {
      onSelectionChange(selectedRecipients.filter((id) => id !== idToRemove))
   }

   const handleSelectContact = (contact: Contact) => {
      onSelectionChange([...selectedRecipients, contact.id])
      setInputValue("")
      inputRef.current?.focus()
   }

   useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
         if (
            wrapperRef.current &&
            !wrapperRef.current.contains(event.target as Node)
         ) {
            setShowDropdown(false)
         }
      }

      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
   }, [])

   return (
      <div
         className="alert-message-setting__contact-autocomplete"
         ref={wrapperRef}
      >
         <div className="alert-message-setting__contact-autocomplete--container">
            <div className="alert-message-setting__contact-autocomplete--input-area">
               {selectedContacts.map((contact) => (
                  <div
                     key={contact.id}
                     className="alert-message-setting__contact-autocomplete--selected-recipient"
                     onClick={() => handleRemoveContact(contact.id)}
                  >
                     {contact.name}
                     <Icon
                        name="cross"
                        size="8"
                        color={IconColor.HONOLULU_BLUE}
                        className="alert-message-setting__contact-autocomplete--chip-close"
                     />
                  </div>
               ))}
               <input
                  ref={inputRef}
                  type="text"
                  className="alert-message-setting__contact-autocomplete--input"
                  value={inputValue}
                  onChange={(e) => {
                     setInputValue(e.target.value)
                     setShowDropdown(true)
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Recipients"
               />
            </div>
         </div>

         {showDropdown && (inputValue || filteredContacts.length > 0) && (
            <div className="alert-message-setting__contact-autocomplete--dropdown">
               {filteredContacts.map((contact) => (
                  <div
                     key={contact.id}
                     onClick={() => {
                        if (selectedRecipients.includes(contact.id)) {
                           handleRemoveContact(contact.id)
                        } else {
                           handleSelectContact(contact)
                        }
                     }}
                  >
                     {selectedRecipients.includes(contact.id) && (
                        <Icon
                           className="alert-message-setting__contact-autocomplete--icon"
                           name="checkmark"
                           size="12"
                           color={IconColor.HONOLULU_BLUE}
                        />
                     )}
                     <span className="alert-message-setting__contact-autocomplete--options">
                        <span className="alert-message-setting__contact-autocomplete--options-name">
                           {contact.name} ({capitalize(contact.role)})
                        </span>{" "}
                        <span className="alert-message-setting__contact-autocomplete--options-value">
                           {messageType === MessageType.EMAIL
                              ? contact.email
                              : contact.phone}
                        </span>
                     </span>
                  </div>
               ))}
               {filteredContacts.length === 0 && inputValue && (
                  <div className="alert-message-setting__contact-autocomplete--no-results">
                     No contacts found
                  </div>
               )}
            </div>
         )}
      </div>
   )
}

export default ContactAutocomplete
